import React, { useState, useEffect, useRef } from 'react';
import { Pencil, Copy, Star, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { SchemaForm } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import { encryptValueJson } from '../../utils/fieldEncryption';
import { updatePageTitle, upsertPage, removePage } from '../../utils/composerPages';
import { isFavorite, toggleFavorite, removeFavorite } from '../../utils/favorites';
import { dateOf } from '../../nucleus/format';
import './NodeComposer.css';

const DEFAULT_TITLE = 'New node';

const SchemaGlyph = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></svg>
);
const schemaLabel = (s) => s?.name || s?.title || 'Schema';

/**
 * Notion-style full-page node editor. The node is already created when this
 * mounts (immediate-create from the sidebar compose flow), so there's no save
 * button — edits autosave. The "Get started with" row (schema) shows while
 * the page is untouched; once you start typing it gives way to a top-right
 * Properties menu holding the same options.
 */
export default function NodeComposer({ node, uid, onLeave }) {
    const { encKeys } = useAuth();
    const [title, setTitle] = useState(node.title || DEFAULT_TITLE);
    const [content, setContent] = useState(node.value_json?.content || '');
    const [schemaId, setSchemaId] = useState(node.schema_id || null);
    const [schemas, setSchemas] = useState([]);
    // Full schema (with json_schema/encrypted_fields) + the node's schema-field
    // values — for the Notion-style "properties from the schema" editor.
    const [fullSchema, setFullSchema] = useState(null);
    const [schemaMetadata, setSchemaMetadata] = useState(() => {
        const { content: _c, ...rest } = node.value_json || {};
        return rest;
    });
    useEffect(() => {
        if (!schemaId) { setFullSchema(null); return; }
        let alive = true;
        api.request(`/schemas/${schemaId}?include_json_schema=true`)
            .then(s => { if (alive) setFullSchema(s); })
            .catch(() => { if (alive) setFullSchema(null); });
        return () => { alive = false; };
    }, [schemaId]);
    const [touched, setTouched] = useState(() =>
        (!!node.title && node.title !== DEFAULT_TITLE) || !!node.value_json?.content || !!node.schema_id
    );
    const [picker, setPicker] = useState(null); // bottom "Get started with": 'schema'
    const [menuOpen, setMenuOpen] = useState(false); // top-right Properties menu

    const titleRef = useRef(null);
    const bodyRef = useRef(null);
    const pickerRef = useRef(null);
    const menuRef = useRef(null);
    const dirtyRef = useRef(false);
    const saveTimer = useRef(null);

    // Focus the title; if it's still the default, select it so typing replaces it.
    useEffect(() => {
        const el = titleRef.current;
        if (!el) return;
        el.focus();
        if ((node.title || DEFAULT_TITLE) === DEFAULT_TITLE) el.select?.();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let alive = true;
        api.getSchemas({ include_json_schema: 'false', limit: 100 }).then(s => { if (alive) setSchemas(s || []); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!picker) return;
        const h = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPicker(null); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [picker]);

    useEffect(() => {
        if (!menuOpen) return;
        const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [menuOpen]);

    const doSave = async () => {
        try {
            let value_json = { ...(node.value_json || {}), ...schemaMetadata };
            if (content) value_json.content = content; else delete value_json.content;
            // Encrypt any schema-declared encrypted fields before saving.
            if (fullSchema?.encrypted_fields?.length && encKeys?.publicKey) {
                value_json = await encryptValueJson(value_json, fullSchema.encrypted_fields, encKeys);
            }
            await api.updateNode(node.id, {
                title: title.trim() || DEFAULT_TITLE,
                value_json,
                ...(schemaId ? { schema_id: schemaId } : {}),
            });
            window.dispatchEvent(new CustomEvent('nodes-changed'));
        } catch { /* ignore */ }
    };

    // Debounced backend autosave — only after the first real edit.
    useEffect(() => {
        if (!dirtyRef.current) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(doSave, 600);
        return () => saveTimer.current && clearTimeout(saveTimer.current);
    }, [title, content, schemaId, schemaMetadata]); // eslint-disable-line react-hooks/exhaustive-deps

    const markDirty = () => { dirtyRef.current = true; if (!touched) setTouched(true); };

    const onTitleChange = (v) => {
        setTitle(v);
        markDirty();
        updatePageTitle(uid, node.id, v.trim() || DEFAULT_TITLE); // live sidebar rename
    };
    const onContentChange = (v) => { setContent(v); markDirty(); };
    const chooseSchema = (id) => { setSchemaId(prev => (String(prev) === String(id) ? null : id)); markDirty(); };

    const selectedSchema = schemas.find(s => String(s.id) === String(schemaId)) || null;

    // Favorite state (kept in sync with the sidebar).
    const [fav, setFav] = useState(false);
    useEffect(() => {
        const upd = () => setFav(isFavorite(uid, 'page', node.id));
        upd();
        window.addEventListener('favorites-changed', upd);
        return () => window.removeEventListener('favorites-changed', upd);
    }, [uid, node.id]);

    // ── Page actions (mirror the sidebar right-click menu) ──
    const handleRename = () => { setMenuOpen(false); const el = titleRef.current; if (el) { el.focus(); el.select?.(); } };
    const handleDuplicate = async () => {
        setMenuOpen(false);
        try {
            const vj = { ...(node.value_json || {}) };
            if (content) vj.content = content;
            const resp = await api.createNode(`${(title.trim() || DEFAULT_TITLE)} (copy)`, vj, schemaId || null);
            const created = resp.node || resp;
            upsertPage(uid, { id: created.id, title: created.title });
            window.dispatchEvent(new CustomEvent('nodes-changed'));
        } catch { /* ignore */ }
    };
    const handleFavorite = () => { toggleFavorite(uid, { type: 'page', id: node.id, name: title.trim() || DEFAULT_TITLE }); setMenuOpen(false); };
    const handleDelete = async () => {
        setMenuOpen(false);
        try { await api.deleteNode(node.id); } catch { /* ignore */ }
        removePage(uid, node.id);
        removeFavorite(uid, 'page', node.id);
        window.dispatchEvent(new CustomEvent('nodes-changed'));
        onLeave();
    };

    const schemaOptions = (
        <div className="node-composer__opts">
            {schemas.length === 0 && <div className="node-composer__popover-empty">No schemas yet</div>}
            {schemas.map(s => (
                <button key={s.id} type="button" className={`btn node-composer__popover-item ${String(s.id) === String(schemaId) ? 'selected' : ''}`} onClick={() => chooseSchema(s.id)}>
                    <span>{schemaLabel(s)}</span>
                    {String(s.id) === String(schemaId) && <span>✓</span>}
                </button>
            ))}
        </div>
    );

    const onKeyDown = (e) => { if (e.key === 'Escape') { e.preventDefault(); onLeave(); } };

    return (
        <div className="node-composer" onKeyDown={onKeyDown}>
            <div className="node-composer__topbar">
                <div className="node-composer__props" ref={menuRef}>
                    <button type="button" className={`btn node-composer__props-btn ${menuOpen ? 'active' : ''}`} onClick={() => setMenuOpen(o => !o)} title="Properties" data-qh="composer-menu">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><circle cx="5" cy="12" r="1.4" fill="currentColor"/><circle cx="12" cy="12" r="1.4" fill="currentColor"/><circle cx="19" cy="12" r="1.4" fill="currentColor"/></svg>
                    </button>
                    {menuOpen && (
                        <div className="node-composer__menu">
                            <div className="node-composer__menu-actions">
                                <button type="button" className="btn node-composer__menu-action" onClick={handleRename}><Pencil size={15} /> <span>Rename</span></button>
                                <button type="button" className="btn node-composer__menu-action" onClick={handleDuplicate}><Copy size={15} /> <span>Duplicate</span></button>
                                <button type="button" className="btn node-composer__menu-action" onClick={handleFavorite}><Star size={15} className={fav ? 'node-composer__star--on' : ''} /> <span>{fav ? 'Remove from favorites' : 'Add to favorites'}</span></button>
                                <button type="button" className="btn node-composer__menu-action node-composer__menu-action--danger" onClick={handleDelete}><Trash2 size={15} /> <span>Delete (move to trash)</span></button>
                            </div>
                            <div className="node-composer__menu-divider" />
                            <div className="node-composer__menu-section">
                                <div className="node-composer__menu-label"><SchemaGlyph /> Schema</div>
                                {schemaOptions}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="node-composer__scroll">
                <div className="node-composer__page">
                    <textarea
                        ref={titleRef}
                        className="node-composer__title"
                        data-qh="composer-title"
                        placeholder={DEFAULT_TITLE}
                        value={title}
                        rows={1}
                        onChange={(e) => onTitleChange(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); bodyRef.current?.focus(); } }}
                    />

                    {/* Rich metadata — Notion-style properties shown in the page. */}
                    <div className="node-composer__props-list" ref={pickerRef}>
                        <div className="node-composer__prop" data-qh="prop-schema">
                            <span className="node-composer__prop-key"><SchemaGlyph /> Schema</span>
                            <div className="node-composer__prop-val">
                                <button type="button" className="btn node-composer__prop-edit" onClick={() => setPicker(p => p === 'schema' ? null : 'schema')}>
                                    {selectedSchema
                                        ? <span className="node-composer__pill"><SchemaGlyph /> {schemaLabel(selectedSchema)}</span>
                                        : <span className="node-composer__prop-empty">Empty</span>}
                                </button>
                                {picker === 'schema' && (
                                    <div className="node-composer__popover node-composer__popover--inline">
                                        <div className="node-composer__popover-title">Choose a schema</div>
                                        {schemaOptions}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="node-composer__prop" data-qh="prop-app">
                            <span className="node-composer__prop-key">App</span>
                            <span className="node-composer__prop-static">{node.created_by_app_name || '—'}</span>
                        </div>
                        <div className="node-composer__prop" data-qh="prop-created">
                            <span className="node-composer__prop-key">Created</span>
                            <span className="node-composer__prop-static">{dateOf(node.created_at)}</span>
                        </div>
                        <div className="node-composer__prop" data-qh="prop-updated">
                            <span className="node-composer__prop-key">Last updated</span>
                            <span className="node-composer__prop-static">{dateOf(node.updated_at)}</span>
                        </div>

                        {/* Schema-defined fields — true Notion "properties from the schema". */}
                        {fullSchema?.json_schema?.properties && (
                            <div className="node-composer__schema-fields" data-qh="composer-fields">
                                <SchemaForm
                                    schema={fullSchema.json_schema}
                                    values={schemaMetadata}
                                    onChange={(key, val) => { setSchemaMetadata(prev => ({ ...prev, [key]: val })); markDirty(); }}
                                    mode="edit"
                                    layout="properties"
                                    encryptedFields={fullSchema.encrypted_fields || []}
                                    encKeys={encKeys}
                                />
                            </div>
                        )}
                    </div>

                    <textarea
                        ref={bodyRef}
                        className="node-composer__body"
                        data-qh="composer-body"
                        placeholder="Add content…"
                        value={content}
                        onChange={(e) => onContentChange(e.target.value)}
                    />
                </div>
            </div>

        </div>
    );
}
